
import { trackClick } from '@marketplace/helpers/segment/index.js'
import { calculateActualMinimumQuantity } from '@marketplace/helpers/quantity-calculations.js'
import debounce from 'lodash/debounce'

const FALLBACK_MAX = 200

export default {
    name: 'ProductQuantity',

    props: {
        inventory: {
            // -1 allows backordering
            type: Number,
            default: -1,
        },
        maxQuantityOptions: {
            type: Number,
            default: 80,
        },
        caseQuantity: {
            type: Number,
            default: 1,
        },
        minimumOrderQuantity: {
            type: Number,
            default: 1,
        },
        initialValue: {
            type: Number,
            default: 0,
        },
        allowZero: {
            type: Boolean,
            default: false,
        },
        isDisabled: {
            type: Boolean,
            default: false,
        },
        isUpdating: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            quantity: 0,
            hovered: false,
            tooltipVisible: false,
        }
    },

    computed: {
        /**
         * If has caseQuantity > 1 its a dropdown selector
         *
         *  @returns {boolean}
         */
        isIncrementor() {
            return this.caseQuantity === 1
        },
        /**
         * Increase is disabled when max is reached
         * -1 means an unlimited amount can be ordered
         *
         * @returns {boolean}
         */
        isIncreaseDisabled() {
            if (this.isDisabled) return true
            if (this.inventory === -1) return false
            return this.quantity >= this.inventory
        },
        /**
         * Decrease is disabled when quantity reaches 0 or 1
         *
         * @returns {boolean}
         */
        isDecreaseDisabled() {
            if (this.isDisabled) return true
            if (this.allowZero) return this.quantity === 0
            return this.quantity <= this.minimumValue
        },
        minimumValue() {
            if (this.allowZero) return 0

            const minimumOrderQuantity = Math.max(1, this.minimumOrderQuantity)
            const caseQuantity = Math.max(1, this.caseQuantity)

            return calculateActualMinimumQuantity(
                minimumOrderQuantity,
                caseQuantity
            )
        },
        /**
         * Created the caseQuantity options for the dropdown
         * If there is a max inventory limit the amount of options to the inventory
         * Otherwise make a max of maxQuantityOptions options in the dropdown
         * FALLBACK_MAX was added to prevent cases where someone added a huge amount of inventory
         * Expected result:
         * [{value: <number>, label: <string> }]
         *
         * @returns {Array}
         */
        caseQuantityOptions() {
            if (this.isIncrementor) return

            const minValue = this.minimumValue
            const startingMultiplier = Math.max(
                1,
                Math.ceil(minValue / this.caseQuantity)
            )

            const maxPossibleOptions =
                this.inventory <= -1
                    ? this.maxQuantityOptions
                    : Math.min(
                          FALLBACK_MAX,
                          Math.floor(this.inventory / this.caseQuantity) -
                              startingMultiplier +
                              1
                      )

            const optionsArray = new Array(Math.max(0, maxPossibleOptions))
                .fill('')
                .map((item, index) => {
                    const quantity =
                        (startingMultiplier + index) * this.caseQuantity

                    return {
                        value: quantity,
                        label: `${quantity} ${
                            quantity === 1
                                ? this.$t('product-page.unit')
                                : this.$t('product-page.units')
                        }`,
                    }
                })

            if (this.allowZero) {
                optionsArray.unshift({
                    value: 0,
                    label: `0 ${this.$t('product-page.units')}`,
                })
            }
            return optionsArray
        },
    },

    watch: {
        initialValue() {
            this.setQuantity()
        },
    },

    beforeMount() {
        this.setQuantity()
    },

    mounted() {
        document.addEventListener('click', this.handleOutsideClick)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleOutsideClick)
    },

    methods: {
        /**
         * Sets the data quantity
         * quantity should be divided by caseQuantity
         */
        setQuantity() {
            // this.quantity = Math.floor(this.initialValue / this.caseQuantity)
            this.quantity = this.initialValue
        },
        /**
         * Updates the changed quantity
         * Function is stopped when there is no change or an invalid value is passed
         *
         * @param {object} payload
         */
        updateQuantity(payload) {
            const value = parseInt(payload?.target?.value) || this.quantity

            if (value === null || isNaN(value)) return

            this.quantity = this.verifyQuantity(value)

            this.emitQuantity()

            this.tooltipVisible = !!(
                this.isDisabled ||
                (!this.isDisabled && !this.isUpdating && this.isIncreaseDisabled)
            )
        },
        /**
         * Accepts a value and checks if it meets the min/max of a quantity field
         *
         * @param {any} quantity
         * @returns {number}
         */
        verifyQuantity(quantity) {
            let parsedQuantity = parseInt(quantity) || this.minimumValue

            // if bigger than the max inventory set it back to max inventory
            // if continue selling (-1) skip this check
            if (this.inventory > 0 && parsedQuantity >= this.inventory) {
                parsedQuantity = this.inventory
            }

            // if less than the minimum or empty set it back to minimum
            if (parsedQuantity < this.minimumValue || quantity === '') {
                return this.minimumValue
            }

            // For incrementor mode (caseQuantity = 1), allow any valid quantity
            if (this.isIncrementor) {
                return parsedQuantity
            }

            // For dropdown mode (caseQuantity > 1), ensure quantity is a valid multiple
            // Find the closest valid multiple of caseQuantity that is >= minimumValue
            const minValue = this.minimumValue
            const remainder = parsedQuantity % this.caseQuantity

            if (remainder === 0 && parsedQuantity >= minValue) {
                return parsedQuantity
            }

            // Round up to the next valid multiple
            const nextMultiple =
                Math.ceil(parsedQuantity / this.caseQuantity) *
                this.caseQuantity
            return Math.max(nextMultiple, minValue)
        },
        /**
         * Emits the quantity
         */
        emitQuantity: debounce(function () {
            this.$emit('quantity-update', this.quantity)
        }, 100),
        /**
         * Decreases the quantity
         * and runs throught the update quantity function
         */
        decreaseQuantity() {
            if (this.isIncrementor) {
                this.quantity = Math.max(
                    this.minimumValue,
                    this.quantity - this.caseQuantity
                )
            } else {
                this.quantity--
            }
            this.updateQuantity()
        },
        /**
         * Increases the quantity
         * and runs throught the update quantity function
         */
        increaseQuantity() {
            if (this.isIncrementor) {
                this.quantity += this.caseQuantity
            } else {
                this.quantity++
            }
            this.updateQuantity()
        },
        /**
         * Rechecks on blur if there is a falsy value in quantity
         * Tracks a change in the quantity
         */
        trackInput: debounce(function () {
            if (!this.quantity) {
                this.quantity = this.verifyQuantity(this.quantity)
            }

            trackClick('Quantity Change', {
                category: this.$helpers.getSegmentCategoryNamebyRoute(
                    this.$page.currentRouteName
                ),
                quantity: this.quantity,
                changeType: this.isIncrementor ? 'number input' : 'dropdown',
            })
        }, 100),
        toggleTooltip() {
            this.tooltipVisible = !!(
                this.isDisabled ||
                (!this.isDisabled && !this.isUpdating && this.isIncreaseDisabled)
            )
        },
        handleOutsideClick(event) {
            if (!this.$el.contains(event.target) && this.tooltipVisible) {
                this.tooltipVisible = false
            }
        },
    },
}
